import {
  advanceHintEscalation,
  createHintEscalationState,
  createRetryFeedback,
  type ActiveHintStage,
  type HintEscalationState,
  type RetryFeedback
} from "@/game/feedback";
import {
  getAdjacentId,
  type InputMethod,
  type NavigationDirection
} from "@/game/input";

import {
  resolveTargetSearchRounds,
  type ResolvedTargetSearchRound,
  type TargetSearchPayload
} from "./schema";

export type TargetSearchStatus = "playing" | "completed";
export type TargetSearchSelectionResult = "target" | "distractor";

export interface TargetSearchSelection {
  readonly roundId: string;
  readonly objectId: string;
  readonly result: TargetSearchSelectionResult;
  readonly inputMethod: InputMethod;
}

export interface TargetSearchState {
  readonly rounds: readonly ResolvedTargetSearchRound[];
  readonly currentRoundIndex: number;
  readonly foundTargetIds: readonly string[];
  readonly focusedId: string | null;
  readonly highlightedTargetId: string | null;
  readonly reducedChoiceIds: readonly string[] | null;
  readonly selections: readonly TargetSearchSelection[];
  readonly hintCount: number;
  readonly hintState: HintEscalationState;
  readonly retryCount: number;
  readonly lastFeedback: RetryFeedback | null;
  readonly lastInputMethod: InputMethod | null;
  readonly status: TargetSearchStatus;
}

export type TargetSearchAction =
  | {
      readonly type: "focus";
      readonly id: string | null;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "move-focus";
      readonly direction: NavigationDirection;
    }
  | {
      readonly type: "select";
      readonly id: string;
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "activate-focused" }
  | { readonly type: "request-hint" }
  | { readonly type: "clear-hint" };

export function createTargetSearchState(
  payload: TargetSearchPayload
): TargetSearchState {
  const rounds = resolveTargetSearchRounds(payload);

  if (rounds.length === 0) {
    throw new Error("Target search requires at least one round");
  }

  return {
    rounds,
    currentRoundIndex: 0,
    foundTargetIds: [],
    focusedId: null,
    highlightedTargetId: null,
    reducedChoiceIds: null,
    selections: [],
    hintCount: 0,
    hintState: createHintEscalationState(),
    retryCount: 0,
    lastFeedback: null,
    lastInputMethod: null,
    status: "playing"
  };
}

export function getActiveTargetSearchRound(
  state: TargetSearchState
): ResolvedTargetSearchRound | null {
  if (state.status === "completed") {
    return null;
  }

  return state.rounds[state.currentRoundIndex] ?? null;
}

export function getTargetSearchFocusableIds(
  state: TargetSearchState
): string[] {
  const round = getActiveTargetSearchRound(state);

  if (round === null) {
    return [];
  }

  const foundIds = new Set(state.foundTargetIds);
  const reducedIds =
    state.reducedChoiceIds === null
      ? null
      : new Set(state.reducedChoiceIds);

  return round.visibleObjects
    .map(({ id }) => id)
    .filter(
      (id) =>
        !foundIds.has(id) &&
        (reducedIds === null || reducedIds.has(id))
    );
}

function getNextUnresolvedTargetId(
  round: ResolvedTargetSearchRound,
  foundTargetIds: readonly string[]
): string | undefined {
  const foundIds = new Set(foundTargetIds);
  return round.targetIds.find((id) => !foundIds.has(id));
}

function getHintChoiceIds(
  round: ResolvedTargetSearchRound,
  foundTargetIds: readonly string[],
  stage: ActiveHintStage
): readonly string[] | null {
  if (stage === "gentle-hint") {
    return null;
  }

  const foundIds = new Set(foundTargetIds);
  const unresolvedTargets = round.targetIds.filter(
    (id) => !foundIds.has(id)
  );

  if (stage === "replay-demonstration") {
    return unresolvedTargets;
  }

  const distractor = round.distractorIds[0];
  return distractor === undefined
    ? unresolvedTargets
    : [...unresolvedTargets, distractor];
}

function selectObject(
  state: TargetSearchState,
  objectId: string,
  inputMethod: InputMethod
): TargetSearchState {
  const round = getActiveTargetSearchRound(state);

  if (
    round === null ||
    !getTargetSearchFocusableIds(state).includes(objectId)
  ) {
    return state;
  }

  if (round.targetIds.includes(objectId)) {
    const foundTargetIds = [...state.foundTargetIds, objectId];
    const selection: TargetSearchSelection = {
      roundId: round.id,
      objectId,
      result: "target",
      inputMethod
    };
    const selectedState: TargetSearchState = {
      ...state,
      foundTargetIds,
      focusedId: null,
      highlightedTargetId:
        state.highlightedTargetId === objectId
          ? null
          : state.highlightedTargetId,
      selections: [...state.selections, selection],
      lastInputMethod: inputMethod,
      lastFeedback: null
    };

    if (foundTargetIds.length < round.targetIds.length) {
      return selectedState;
    }

    const nextRoundIndex = state.currentRoundIndex + 1;

    if (state.rounds[nextRoundIndex] !== undefined) {
      return {
        ...selectedState,
        currentRoundIndex: nextRoundIndex,
        foundTargetIds: [],
        highlightedTargetId: null,
        reducedChoiceIds: null,
        hintState: createHintEscalationState(),
        lastFeedback: null
      };
    }

    return {
      ...selectedState,
      highlightedTargetId: null,
      reducedChoiceIds: null,
      status: "completed"
    };
  }

  if (round.distractorIds.includes(objectId)) {
    const retryTransition = createRetryFeedback(state.hintState);
    const highlightedTargetId = getNextUnresolvedTargetId(
      round,
      state.foundTargetIds
    );

    return {
      ...state,
      focusedId: objectId,
      highlightedTargetId: highlightedTargetId ?? null,
      reducedChoiceIds: getHintChoiceIds(
        round,
        state.foundTargetIds,
        retryTransition.feedback.hintStage
      ),
      selections: [
        ...state.selections,
        {
          roundId: round.id,
          objectId,
          result: "distractor",
          inputMethod
        }
      ],
      hintCount: state.hintCount + 1,
      hintState: retryTransition.hintState,
      retryCount: state.retryCount + 1,
      lastFeedback: retryTransition.feedback,
      lastInputMethod: inputMethod
    };
  }

  return state;
}

export function reduceTargetSearch(
  state: TargetSearchState,
  action: TargetSearchAction
): TargetSearchState {
  if (state.status === "completed") {
    return state;
  }

  switch (action.type) {
    case "focus": {
      if (
        action.id !== null &&
        !getTargetSearchFocusableIds(state).includes(action.id)
      ) {
        return state;
      }

      return {
        ...state,
        focusedId: action.id,
        lastInputMethod: action.inputMethod
      };
    }
    case "move-focus": {
      const focusedId = getAdjacentId(
        getTargetSearchFocusableIds(state),
        state.focusedId,
        action.direction
      );

      if (focusedId === null) {
        return state;
      }

      return {
        ...state,
        focusedId,
        lastInputMethod: "keyboard"
      };
    }
    case "select":
      return selectObject(state, action.id, action.inputMethod);
    case "activate-focused":
      return state.focusedId === null
        ? state
        : selectObject(state, state.focusedId, "keyboard");
    case "request-hint": {
      const round = getActiveTargetSearchRound(state);

      if (round === null) {
        return state;
      }

      const highlightedTargetId = getNextUnresolvedTargetId(
        round,
        state.foundTargetIds
      );

      if (highlightedTargetId === undefined) {
        return state;
      }

      const hintState = advanceHintEscalation(state.hintState);

      return {
        ...state,
        highlightedTargetId,
        reducedChoiceIds: getHintChoiceIds(
          round,
          state.foundTargetIds,
          hintState.stage
        ),
        hintCount: state.hintCount + 1,
        hintState,
        lastFeedback: null
      };
    }
    case "clear-hint":
      return state.highlightedTargetId === null &&
        state.reducedChoiceIds === null &&
        state.lastFeedback === null
        ? state
        : {
            ...state,
            highlightedTargetId: null,
            reducedChoiceIds: null,
            lastFeedback: null
          };
  }
}
